// WsProvider owns a single WsClient for the entire React tree. The client is
// a module-scope singleton created connected at import time (see
// sharedWsClient below), so the handshake overlaps bundle evaluation instead
// of starting after React's first commit; the StrictMode-safe destroy timer
// still guards real unmounts.
//
// Note: we *don't* hand the client to consumers via `useState` because
// the client identity must be stable across re-renders (so subscribe()
// returned cleanup closures stay valid). A plain module-scope-ish ref
// works because the provider mounts exactly once at the app root.

import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react'
import { WsClient } from './WsClient'

interface WsLocation {
  protocol: string
  hostname: string
  host?: string
}

function currentWsLocation(): WsLocation {
  if (typeof window !== 'undefined' && window.location) {
    return window.location
  }
  return { protocol: 'http:', hostname: 'localhost' }
}

export function defaultWsUrl(
  loc: WsLocation = currentWsLocation(),
  configuredUrl?: string,
  development = import.meta.env.DEV
): string {
  const configured = configuredUrl?.trim()
  if (configured && /^wss?:\/\//i.test(configured)) return configured

  const proto = loc.protocol === 'https:' ? 'wss' : 'ws'
  const hostname = loc.hostname || 'localhost'
  if (
    development &&
    (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]')
  ) {
    return `${proto}://${hostname}:8082/ws`
  }
  return `${proto}://${loc.host || hostname}/ws`
}

const WS_URL = defaultWsUrl(
  currentWsLocation(),
  import.meta.env.VITE_WS_URL as string | undefined,
  import.meta.env.DEV
)

const WsContext = createContext<WsClient | null>(null)

// One app-wide client, created and CONNECTED at module evaluation — while the
// bundle is still loading, before React boots — so the socket handshake and
// the first subscribe ACKs are not serialized behind React's first commit.
// WsClient.connect() is idempotent, subscriptions installed while CONNECTING
// are replayed on open, and reconnect/backoff semantics are unchanged.
let sharedClient: WsClient | null = null

export function sharedWsClient(): WsClient {
  if (!sharedClient || sharedClient.isDestroyed) {
    sharedClient = new WsClient(WS_URL)
    sharedClient.connect()
  }
  return sharedClient
}

// Test runners evaluate modules long before rendering and must not leak real
// sockets/reconnect timers; tests mount WsProvider explicitly instead.
if (import.meta.env.MODE !== 'test' && typeof WebSocket !== 'undefined') {
  sharedWsClient()
}

export function WsProvider({ children }: { children: ReactNode }) {
  // Construct (and connect) synchronously so child feeds can install and ACK
  // subscriptions before their first REST seed.
  const [client] = useState(sharedWsClient)
  const destroyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // React StrictMode intentionally replays effects as setup -> cleanup -> setup.
    // Deferring the terminal destroy by one task lets the replayed setup cancel it,
    // while a real provider unmount still tears the socket down promptly. A later
    // remount gets a fresh connected client from sharedWsClient().
    if (destroyTimer.current !== null) {
      clearTimeout(destroyTimer.current)
      destroyTimer.current = null
    }
    // Coming back to a backgrounded tab is the one moment the socket is most
    // likely to be dead while still LOOKING alive — a phone suspends the page
    // and the carrier drops the connection without a close event. Tell the
    // client so it can floor its backoff and probe, instead of leaving the tape
    // frozen for ~10s (see WsClient.resumeNow). pageshow covers the bfcache
    // restore, which fires no visibilitychange at all.
    const resume = () => {
      if (document.visibilityState === 'visible') client.resumeNow()
    }
    document.addEventListener('visibilitychange', resume)
    window.addEventListener('pageshow', resume)
    window.addEventListener('online', resume)

    return () => {
      document.removeEventListener('visibilitychange', resume)
      window.removeEventListener('pageshow', resume)
      window.removeEventListener('online', resume)
      destroyTimer.current = setTimeout(() => {
        destroyTimer.current = null
        client.destroy()
      }, 0)
    }
  }, [client])

  return <WsContext.Provider value={client}>{children}</WsContext.Provider>
}

// useWs returns the live client. We deliberately throw rather than
// return `null` so callers can't silently no-op when used outside the
// provider — the failure mode would otherwise be "subscriptions look
// fine in code but nothing ever fires".
export function useWs(): WsClient {
  const c = useContext(WsContext)
  if (!c) {
    throw new Error('useWs must be used inside <WsProvider>')
  }
  return c
}

// Variant that returns null while the provider is still mounting. Use
// this in hooks that want to gracefully no-op on the very first render
// (before the WsProvider's useEffect has run).
export function useWsOptional(): WsClient | null {
  return useContext(WsContext)
}
